import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { IndicacaoService } from '../../../services/indicacoes/indicacao.service';
import {
  ComissaoConsultaDTO,
  ConsultaIndicacaoMasterDTO,
  CriarCupomMasterDTO,
  CupomMasterItemDTO,
  EmpresaBuscaMasterDTO,
  IndicacaoConsultaDTO,
  OrigemIndicacao,
  PercentualPadraoCupomDTO,
  RevogarCuponsMasterDTO,
  SelecaoCupomIndicacaoConsultaDTO,
  StatusIndicacao,
  StatusRepasse,
  StatusRepasseResumo,
} from '../../../models/indicacoes/indicacao.model';
import { CategoriaPlano } from '../../../models/planos/plano.model';

/** Severities aceitas pelo p-tag do PrimeNG. */
type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';

/**
 * Area Master — Cupons de indicacao.
 *
 * Era a aba "Dados dos cupons" da tela de Indicacoes; virou tela propria porque so o master
 * a enxerga. Concentra:
 * - listagem paginada de TODOS os cupons (busca por codigo/empresa, ordenacao server-side);
 * - dialog "Novo cupom" (inclusive o cupom ESPECIAL, que define o plano em vez de descontar);
 * - drill-down: ao abrir um cupom, consulta os referidos daquele escopo (consulta master) e
 *   permite ver as competencias de cada indicacao.
 *
 * O repasse de comissao NAO acontece aqui: e sempre um detalhe de OUTRA empresa, fora do
 * escopo da consulta (mesma regra que a aba antiga aplicava via `detalheMaster`).
 *
 * Acesso restrito via `masterAccessGuard` na rota `/master/cupons`.
 * Fonte da verdade: `vendas/docs/api/API_CONTRACT_indicacoes.md`.
 */
@Component({
  selector: 'app-master-cupons',
  templateUrl: './master-cupons.component.html',
  // Reaproveita a folha de Indicacoes: os cards, a tabela de referidos e os dialogs
  // wave sao os mesmos de la (esta tela saiu daquela). Duplicar as ~1000 linhas so
  // criaria duas copias para manter em sincronia.
  // A segunda folha traz so o que esta tela tem a mais: a selecao em lote da tabela.
  styleUrls: ['../../indicacoes/indicacoes.component.scss', './master-cupons.component.scss'],
  providers: [MessageService],
})
export class MasterCuponsComponent implements OnInit, OnDestroy {
  // ===========================================================================
  // Listagem de cupons — todos os cupons paginados
  // ===========================================================================

  cupons: CupomMasterItemDTO[] = [];
  carregandoCupons = true;
  firstCupons = 0;
  rowsCupons = 10;
  totalCupons = 0;
  /** Campo de ordenacao da lista de cupons (campo do backend); null = sem
   *  ordenacao de header (mantem o default do backend: `codigo` asc). Setado ao
   *  clicar num `th` ordenavel (apenas Codigo/Desconto/Comissao). */
  private sortFieldCupons: string | null = null;
  /** Direcao da ordenacao dos cupons: 1 = asc, -1 = desc, 0 = sem ordenacao. */
  private sortOrderCupons = 0;
  /** Termo de busca da lista de cupons (filtra por codigo ou empresa). */
  cuponsBusca = '';
  /** Emissor da busca de cupons (debounce antes de consultar o backend). */
  private cuponsBuscaSubject = new Subject<string>();
  private cuponsBuscaSub?: Subscription;

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // ===========================================================================
  // Consulta master (referidos do cupom / da empresa dona dele)
  // ===========================================================================

  /** Codigo/cupom em consulta (ex.: "AVDP814Z"). Preenchido por `abrirCupom`. */
  cupomBusca = '';
  /** Empresa-alvo quando a consulta parte de uma empresa (tem prioridade sobre o cupom). */
  empresaSelecionada: EmpresaBuscaMasterDTO | null = null;
  /** Sugestoes do autocomplete de empresas (dialog "Novo cupom"). */
  empresasSugestoes: EmpresaBuscaMasterDTO[] = [];
  /** Resultado da consulta master (null = lista de cupons em tela). */
  resultadoMaster: ConsultaIndicacaoMasterDTO | null = null;
  carregandoMaster = false;
  erroMaster = false;
  /** Paginacao da tabela master. Tamanho de pagina segue o default do contrato (15). */
  firstMaster = 0;
  rowsMaster = 15;
  totalMaster = 0;

  /**
   * Seletor "Geral × cupom" da consulta master (consulta_escopo_cupom / FE-2).
   * Opcoes: "Geral" (value=null) + cada cupom da empresa-alvo (res.cupons).
   */
  escopoMasterOptions: { label: string; value: number | null }[] = [];
  /** Selecao atual do seletor master. null = GERAL. Reflete res.cupomId. */
  cupomMasterSelecionadoId: number | null = null;

  // ===========================================================================
  // Dialog "Novo cupom"
  // ===========================================================================

  cupomDialogVisivel = false;
  cupomSubmetido = false;
  salvandoCupom = false;
  cupomForm = this.cupomFormVazio();
  /** Opcoes do dropdown de tipo de repasse (rotulos localizados). */
  tipoPeriodoOptions: { label: string; value: 'MENSAL' | 'A_VISTA' }[] = [];
  /**
   * Opcoes do dropdown de categoria do cupom especial. Rotulos FIXOS (Bronce/Plata/Oro):
   * sao os nomes dos planos como o cliente os ve na vitrine, nao texto traduzivel.
   */
  categoriaPlanoOptions: { label: string; value: CategoriaPlano }[] = [];

  // ===========================================================================
  // Selecao em lote / revogacao de cupons
  // ===========================================================================

  /** Cupons marcados na tabela. Revogar = encerrar a validade deles agora. */
  cuponsSelecionados: CupomMasterItemDTO[] = [];
  revogarDialogVisivel = false;
  revogandoCupons = false;

  // ===========================================================================
  // Dialog "Percentual padrao" dos cupons automaticos
  // ===========================================================================

  padraoDialogVisivel = false;
  padraoSubmetido = false;
  /** Busca dos percentuais vigentes ao abrir o dialog. */
  carregandoPadrao = false;
  salvandoPadrao = false;
  /** Percentuais vigentes no backend (referencia para o aviso de renomeacao). */
  percentualPadraoVigente: PercentualPadraoCupomDTO | null = null;
  padraoForm: { porcentagemDesconto: number | null; porcentagemComissao: number | null } = {
    porcentagemDesconto: null,
    porcentagemComissao: null,
  };

  // ===========================================================================
  // Dialog de detalhe (drill-down das competencias)
  // ===========================================================================

  detalheDialogVisivel = false;
  indicacaoSelecionada: IndicacaoConsultaDTO | null = null;
  comissoes: ComissaoConsultaDTO[] = [];
  carregandoComissoes = false;

  private langChangeSub?: Subscription;

  constructor(
    private indicacaoService: IndicacaoService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  // ===========================================================================
  // Ciclo de vida
  // ===========================================================================

  ngOnInit(): void {
    this.buildTipoPeriodoOptions();
    this.buildCategoriaPlanoOptions();
    this.carregarCupons(0);
    // Busca com debounce (dispara ~500ms apos parar de digitar).
    this.cuponsBuscaSub = this.cuponsBuscaSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => this.carregarCupons(0));

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      // Reconstroi os rotulos do dropdown de tipo de repasse ao trocar de idioma.
      this.buildTipoPeriodoOptions();
      // Reconstroi o rotulo "Geral" do seletor master se houver consulta aberta.
      if (this.resultadoMaster) {
        this.escopoMasterOptions = this.buildEscopoMasterOptions(this.resultadoMaster.cupons ?? []);
      }
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    this.cuponsBuscaSub?.unsubscribe();
  }

  // ===========================================================================
  // Listagem de cupons
  // ===========================================================================

  /** Dispara a busca da lista de cupons (debounced). */
  onBuscaCuponsChange(): void {
    this.cuponsBuscaSubject.next(this.cuponsBusca);
  }

  /**
   * Monta a string de ordenacao (`campo,asc|desc`) para o backend. Sem sort de
   * header retorna undefined => buildQueryParams descarta e o backend aplica o
   * default (`codigo` asc), preservando o comportamento atual.
   */
  private montarSortCupons(): string | undefined {
    if (!this.sortFieldCupons || this.sortOrderCupons === 0) {
      return undefined;
    }
    return `${this.sortFieldCupons},${this.sortOrderCupons === 1 ? 'asc' : 'desc'}`;
  }

  /**
   * Ordenacao por cabecalho da lista de cupons. A p-table usa [customSort] (sem
   * reordenar em memoria — os dados sao server-side) e apenas emite (onSort) com
   * o campo/direcao ja resolvidos. Volta para a primeira pagina e recarrega.
   */
  onSortCupons(event: { field?: string | string[]; order?: number }): void {
    const campo = typeof event.field === 'string' ? event.field : null;
    const ordem = campo ? (event.order ?? 0) : 0;
    // Guarda de mudanca real: numa p-table NAO-lazy com [customSort], o PrimeNG
    // re-executa sortSingle() e re-emite (onSort) com o MESMO field/order toda
    // vez que [value] muda (nossa recarga troca `cupons`). Sem esta guarda isso
    // vira loop de requests + NG0100 (carregandoCupons alternando no ciclo de
    // change detection). So reagimos quando (campo, ordem) realmente muda — o
    // 3o clique (single sort volta a asc no mesmo campo) tambem passa por aqui,
    // pois `ordem` muda de -1 para 1.
    if (campo === this.sortFieldCupons && ordem === this.sortOrderCupons) {
      return;
    }
    this.sortFieldCupons = campo;
    this.sortOrderCupons = ordem;
    this.firstCupons = 0;
    this.carregarCupons(0);
  }

  /** Carrega a pagina informada da listagem de cupons (com o filtro de busca e a ordenacao atuais). */
  carregarCupons(page: number): void {
    this.carregandoCupons = true;
    const termo = this.cuponsBusca.trim() || undefined;
    const sort = this.montarSortCupons();
    this.indicacaoService.listarCuponsMaster(page, this.rowsCupons, termo, sort).subscribe({
      next: (res) => {
        this.cupons = res.content || [];
        this.totalCupons = res.totalElements ?? 0;
        this.firstCupons = page * this.rowsCupons;
        this.carregandoCupons = false;
        // A selecao vale para as linhas em tela: trocar de pagina/filtro a zera, para o
        // master nunca revogar um cupom que ele nao esta mais vendo.
        this.cuponsSelecionados = [];
      },
      error: () => {
        this.cupons = [];
        this.totalCupons = 0;
        this.carregandoCupons = false;
        this.cuponsSelecionados = [];
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('indicacoes.cupons.erro_carregar'),
        });
      },
    });
  }

  /** Troca de pagina da listagem de cupons (paginacao server-side). */
  onPageChangeCupons(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rowsCupons;
    const page = event.page ?? 0;
    this.rowsCupons = size;
    this.carregarCupons(page);
  }

  /** Troca a quantidade de cupons por pagina e volta para a primeira pagina. */
  onRowsPerPageChangeCupons(): void {
    this.firstCupons = 0;
    this.carregarCupons(0);
  }

  /**
   * Abre o detalhe (consulta master) de um cupom da listagem no escopo do proprio
   * cupom clicado (D1): consulta por CODIGO, pois `CupomMasterItemDTO` nao expoe
   * `cupomId`. O backend resolve empresa + escopo=CUPOM pelo codigo (RN03), e a
   * resposta (escopo/cupomId/cupons) pre-seleciona o cupom no seletor master.
   * Aplica-se tanto a cupons vinculados a uma empresa quanto a cupons
   * PROMOCIONAIS (sem empresa dona): o backend resolve o cupom pelo codigo e
   * abre no escopo do proprio cupom promocional (empresaId=null, referidos do cupom).
   */
  abrirCupom(item: CupomMasterItemDTO): void {
    this.empresaSelecionada = null;
    this.cupomBusca = item.codigoIndicacao;
    this.consultarMaster();
  }

  // ===========================================================================
  // Consulta master
  // ===========================================================================

  /** completeMethod do autocomplete de empresas (busca por nome/e-mail). */
  buscarEmpresas(event: { query: string }): void {
    const busca = (event?.query || '').trim();
    if (!busca) {
      this.empresasSugestoes = [];
      return;
    }
    this.indicacaoService.buscarEmpresasMaster(busca).subscribe({
      next: (res) => {
        this.empresasSugestoes = res || [];
      },
      error: () => {
        this.empresasSugestoes = [];
      },
    });
  }

  /** Dispara a consulta master a partir do cupom aberto (volta a primeira pagina). */
  consultarMaster(): void {
    // Nova consulta: o escopo e reposicionado pela resposta (res.cupomId).
    this.cupomMasterSelecionadoId = null;
    this.firstMaster = 0;
    this.carregarConsultaMaster(0);
  }

  /**
   * Executa a consulta master para a pagina informada.
   *
   * - `empresaIdResolvida` presente (troca de escopo / paginacao): ancora a
   *   consulta na empresa ja resolvida (`cupom = undefined`), evitando re-resolver
   *   pelo codigo e cair em empresa errada (consulta_escopo_cupom / FE-2).
   * - `empresaIdResolvida` ausente (nova consulta): empresa selecionada tem
   *   prioridade; senao o cupom aberto.
   */
  private carregarConsultaMaster(
    page: number,
    cupomId?: number,
    empresaIdResolvida?: number,
  ): void {
    const ancorada = empresaIdResolvida !== undefined;
    const cupom = ancorada ? undefined : (this.cupomBusca || '').trim();
    const empresaId = ancorada ? empresaIdResolvida : this.empresaSelecionada?.id;

    if (!ancorada && !empresaId && !cupom) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('indicacoes.consulta_master.erros.informe_cupom_ou_empresa'),
      });
      return;
    }

    this.carregandoMaster = true;
    this.erroMaster = false;

    this.indicacaoService
      .consultarMaster({
        empresaId: empresaId ?? undefined,
        cupom: empresaId ? undefined : cupom,
        cupomId,
        page,
        size: this.rowsMaster,
        sort: 'id,desc',
      })
      .subscribe({
        next: (res) => {
          this.resultadoMaster = res;
          this.totalMaster = res.indicacoes?.totalElements ?? 0;
          this.firstMaster = page * this.rowsMaster;
          // Seletor "Geral × cupom" reflete o escopo retornado (RF06/RF07/RF08).
          this.escopoMasterOptions = this.buildEscopoMasterOptions(res.cupons ?? []);
          this.cupomMasterSelecionadoId = res.cupomId ?? null;
          this.carregandoMaster = false;
        },
        error: (err) => {
          this.carregandoMaster = false;
          // Re-consulta ancorada (troca de escopo / pagina): mantem a tela
          // ("nao pisca") e reverte o seletor ao ultimo escopo valido (OBS-1).
          if (ancorada) {
            this.cupomMasterSelecionadoId = this.resultadoMaster?.cupomId ?? null;
            this.messageService.add({
              severity: 'error',
              summary:
                err?.error?.message ||
                this.translate.instant('indicacoes.consulta_master.erros.nao_encontrado'),
            });
            return;
          }
          // Nova consulta: limpa a tela e volta para a lista de cupons.
          this.resultadoMaster = null;
          this.totalMaster = 0;
          this.firstMaster = 0;
          this.escopoMasterOptions = [];
          this.cupomMasterSelecionadoId = null;
          this.erroMaster = true;
          this.messageService.add({
            severity: 'error',
            summary:
              err?.error?.message ||
              this.translate.instant('indicacoes.consulta_master.erros.nao_encontrado'),
          });
        },
      });
  }

  /**
   * "Geral" (null) + cada cupom da empresa-alvo. O seletor master lista TODOS os
   * cupons (inclusive vencidos); os vencidos recebem o sufixo "(vencido)" no rotulo.
   */
  private buildEscopoMasterOptions(
    cupons: SelecaoCupomIndicacaoConsultaDTO[],
  ): { label: string; value: number | null }[] {
    return [
      { label: this.translate.instant('indicacoes.seletor.geral'), value: null },
      ...cupons.map((c) => ({
        label: this.cupomVencido(c.dataValidade)
          ? `${c.codigoIndicacao} (${this.translate.instant('indicacoes.cupom_detalhe.vencido')})`
          : c.codigoIndicacao,
        value: c.cupomId as number | null,
      })),
    ];
  }

  /**
   * Troca de escopo no seletor master (FE-2): re-consulta ANCORADA na empresa ja
   * resolvida (nunca no codigo digitado), voltando a primeira pagina.
   */
  onEscopoMasterChange(): void {
    if (!this.resultadoMaster) {
      return;
    }
    this.firstMaster = 0;
    // Cupom PROMOCIONAL: empresaId e null -> a re-consulta nao pode ancorar em
    // empresa (cupomId sozinho => 422). `?? undefined` remove a ancora e o alvo
    // e re-resolvido pelo codigo do cupom (cupomBusca), que identifica o
    // promocional sem ambiguidade. Para empresas, mantem a ancora por empresaId.
    this.carregarConsultaMaster(
      0,
      this.cupomMasterSelecionadoId ?? undefined,
      this.resultadoMaster.empresaId ?? undefined,
    );
  }

  /** Fecha a consulta e volta para a listagem de cupons. */
  limparConsultaMaster(): void {
    this.resultadoMaster = null;
    this.erroMaster = false;
    this.cupomBusca = '';
    this.empresaSelecionada = null;
    this.empresasSugestoes = [];
    this.firstMaster = 0;
    this.totalMaster = 0;
    // Reseta o seletor "Geral × cupom" da consulta master (FE-2 / RF09).
    this.escopoMasterOptions = [];
    this.cupomMasterSelecionadoId = null;
  }

  /** Troca de pagina da tabela master (paginacao server-side, preserva o escopo). */
  onPageChangeMaster(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rowsMaster;
    const page = event.page ?? 0;
    this.rowsMaster = size;
    // Preserva o escopo entre paginas, ancorando na empresa ja resolvida (RF05).
    // Cupom PROMOCIONAL (empresaId null): `?? undefined` remove a ancora e o
    // alvo e re-resolvido pelo codigo (cupomBusca) — cupomId sozinho => 422.
    this.carregarConsultaMaster(
      page,
      this.cupomMasterSelecionadoId ?? undefined,
      this.resultadoMaster?.empresaId ?? undefined,
    );
  }

  /** True quando a empresa consultada ja possui codigo/link de indicacao. */
  get temCodigoMaster(): boolean {
    return !!this.resultadoMaster?.codigoIndicacao;
  }

  // ===========================================================================
  // Dialog de detalhe (drill-down)
  // ===========================================================================

  /** Abre o detalhe (drill-down) de uma indicacao da consulta master. */
  abrirDetalheMaster(indicacao: IndicacaoConsultaDTO): void {
    this.carregarDetalhe(indicacao, this.indicacaoService.listarComissoesMaster(indicacao.id));
  }

  /** Carrega as competencias no dialog de detalhe. */
  private carregarDetalhe(
    indicacao: IndicacaoConsultaDTO,
    fonte: Observable<ComissaoConsultaDTO[]>,
  ): void {
    this.indicacaoSelecionada = indicacao;
    this.comissoes = [];
    this.detalheDialogVisivel = true;
    this.carregandoComissoes = true;

    fonte.subscribe({
      next: (res) => {
        this.comissoes = res || [];
        this.carregandoComissoes = false;
      },
      error: (err) => {
        this.carregandoComissoes = false;
        if (err?.status === 404) {
          this.detalheDialogVisivel = false;
          this.toastErro(
            err?.error?.message || this.translate.instant('indicacoes.erros.indicacao_nao_encontrada'),
          );
          return;
        }
        this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
      },
    });
  }

  fecharDetalhe(): void {
    this.detalheDialogVisivel = false;
    this.indicacaoSelecionada = null;
    this.comissoes = [];
  }

  // ===========================================================================
  // Novo cupom
  // ===========================================================================

  private cupomFormVazio(): {
    codigo: string;
    porcentagemDesconto: number | null;
    porcentagemComissao: number | null;
    tipoPeriodo: 'MENSAL' | 'A_VISTA';
    dataValidade: Date | null;
    empresa: EmpresaBuscaMasterDTO | null;
    /** Liga o bloco de cupom especial: o cupom passa a DEFINIR o plano em vez de descontar. */
    especial: boolean;
    categoriaPlano: CategoriaPlano | null;
    valorPlano: number | null;
    periodoDias: number | null;
    limiteDeGasto: number | null;
  } {
    return {
      codigo: '',
      porcentagemDesconto: null,
      porcentagemComissao: null,
      tipoPeriodo: 'MENSAL',
      dataValidade: null,
      empresa: null,
      especial: false,
      categoriaPlano: null,
      valorPlano: null,
      periodoDias: null,
      limiteDeGasto: null,
    };
  }

  /**
   * Texto do icone de cupom ESPECIAL na listagem: resume a condicao negociada, para o master
   * nao precisar abrir o detalhe so para saber de que plano se trata.
   */
  tooltipCupomEspecial(cupom: CupomMasterItemDTO): string {
    const partes: string[] = [this.translate.instant('indicacoes.cupons.especial')];
    if (cupom.categoriaPlano) {
      partes.push(this.rotuloCategoriaPlano(cupom.categoriaPlano));
    }
    if (cupom.semCobranca) {
      partes.push(this.translate.instant('indicacoes.cupons.sem_cobranca'));
    }
    if (cupom.periodoDias) {
      partes.push(this.translate.instant('indicacoes.cupons.periodo_dias', { dias: cupom.periodoDias }));
    }
    return partes.join(' · ');
  }

  /** Nome da faixa como o cliente a ve na vitrine (o enum e interno). */
  rotuloCategoriaPlano(categoria: CategoriaPlano): string {
    return this.categoriaPlanoOptions.find((o) => o.value === categoria)?.label ?? categoria;
  }

  /** Opcoes do dropdown de categoria (rotulos como o cliente ve na vitrine). */
  private buildCategoriaPlanoOptions(): void {
    this.categoriaPlanoOptions = [
      { label: 'Bronce', value: 'BRONZE' },
      { label: 'Plata', value: 'PRATA' },
      { label: 'Oro', value: 'OURO' },
    ];
  }

  /** Reconstroi as opcoes do tipo de repasse (rotulos localizados). */
  private buildTipoPeriodoOptions(): void {
    this.tipoPeriodoOptions = [
      { label: this.tipoPeriodoLabel('MENSAL'), value: 'MENSAL' },
      { label: this.tipoPeriodoLabel('A_VISTA'), value: 'A_VISTA' },
    ];
  }

  abrirNovoCupom(): void {
    this.cupomForm = this.cupomFormVazio();
    this.cupomSubmetido = false;
    this.cupomDialogVisivel = true;
  }

  fecharNovoCupom(): void {
    this.cupomDialogVisivel = false;
  }

  /** Cria o cupom (converte o form em DTO) e recarrega a lista no sucesso. */
  salvarCupom(): void {
    this.cupomSubmetido = true;
    const f = this.cupomForm;
    // No cupom especial o percentual nao se aplica (o valor negociado ja e o final), entao
    // ele deixa de ser obrigatorio e no lugar exigimos as quatro condicoes.
    if (!f.codigo.trim() || f.porcentagemComissao == null || (!f.especial && f.porcentagemDesconto == null)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('indicacoes.novo_cupom.campos_obrigatorios'),
      });
      return;
    }
    if (f.especial && (f.categoriaPlano == null || f.valorPlano == null
        || f.periodoDias == null || f.limiteDeGasto == null)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('indicacoes.novo_cupom.campos_especiais_obrigatorios'),
      });
      return;
    }

    const dto: CriarCupomMasterDTO = {
      codigo: f.codigo.trim(),
      // O backend zera o percentual do cupom especial; mandamos zero para os dois lados
      // contarem a mesma historia.
      porcentagemDesconto: f.especial ? 0 : f.porcentagemDesconto!,
      porcentagemComissao: f.porcentagemComissao,
      tipoPeriodo: f.tipoPeriodo,
      dataValidade: f.dataValidade ? this.toIsoDate(f.dataValidade) : null,
      empresaId: f.empresa?.id ?? null,
      categoriaPlano: f.especial ? f.categoriaPlano : null,
      valorPlano: f.especial ? f.valorPlano : null,
      periodoDias: f.especial ? f.periodoDias : null,
      limiteDeGasto: f.especial ? f.limiteDeGasto : null,
    };

    this.salvandoCupom = true;
    this.indicacaoService.criarCupomMaster(dto).subscribe({
      next: () => {
        this.salvandoCupom = false;
        this.cupomDialogVisivel = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('indicacoes.novo_cupom.sucesso'),
        });
        this.carregarCupons(0);
      },
      error: (err) => {
        this.salvandoCupom = false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('indicacoes.novo_cupom.erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Revogacao de cupons em lote
  // ===========================================================================

  /**
   * Cupom que ainda pode ser revogado: só faz sentido encerrar a validade de quem ainda
   * está valendo. "Sem expiração" (sentinela ano 2100) também é revogável.
   */
  cupomRevogavel(cupom: CupomMasterItemDTO): boolean {
    return !this.cupomVencido(cupom.dataValidade);
  }

  /**
   * Passado ao `[rowSelectable]` da p-table. Arrow function de propriedade porque o
   * PrimeNG chama o callback fora do contexto do componente — um método comum perderia
   * o `this`.
   */
  isCupomSelecionavel = (evento: { data: CupomMasterItemDTO; index: number }): boolean =>
    this.cupomRevogavel(evento.data);

  abrirRevogarCupons(): void {
    if (this.cuponsSelecionados.length === 0) {
      return;
    }
    this.revogarDialogVisivel = true;
  }

  fecharRevogarCupons(): void {
    this.revogarDialogVisivel = false;
  }

  /**
   * Encerra a validade dos cupons marcados. A lista é recarregada no sucesso: os cupons
   * revogados passam a aparecer com a data de hoje na coluna Validade.
   */
  confirmarRevogarCupons(): void {
    const dto: RevogarCuponsMasterDTO = {
      cupomIds: this.cuponsSelecionados.map((c) => c.cupomId),
    };
    if (dto.cupomIds.length === 0) {
      return;
    }

    this.revogandoCupons = true;
    this.indicacaoService.revogarCuponsMaster(dto).subscribe({
      next: (res) => {
        this.revogandoCupons = false;
        this.revogarDialogVisivel = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('indicacoes.revogar_cupons.sucesso'),
          detail: this.translate.instant('indicacoes.revogar_cupons.sucesso_detalhe', {
            revogados: res.revogados,
            ignorados: res.ignorados,
          }),
        });
        this.carregarCupons(this.firstCupons / this.rowsCupons);
      },
      error: (err) => {
        this.revogandoCupons = false;
        this.messageService.add({
          severity: 'error',
          summary:
            err?.error?.message || this.translate.instant('indicacoes.revogar_cupons.erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Percentual padrao dos cupons automaticos
  // ===========================================================================

  /** Abre o dialog ja carregando os percentuais vigentes do backend. */
  abrirPercentualPadrao(): void {
    this.padraoSubmetido = false;
    this.padraoDialogVisivel = true;
    this.carregandoPadrao = true;
    this.indicacaoService.consultarPercentualPadraoCupom().subscribe({
      next: (padrao) => {
        this.carregandoPadrao = false;
        this.percentualPadraoVigente = padrao;
        this.padraoForm = {
          porcentagemDesconto: padrao.porcentagemDesconto,
          porcentagemComissao: padrao.porcentagemComissao,
        };
      },
      error: (err) => {
        this.carregandoPadrao = false;
        this.padraoDialogVisivel = false;
        this.messageService.add({
          severity: 'error',
          summary:
            err?.error?.message ||
            this.translate.instant('indicacoes.percentual_padrao.erro_carregar'),
        });
      },
    });
  }

  fecharPercentualPadrao(): void {
    this.padraoDialogVisivel = false;
  }

  /**
   * O desconto digitado difere do vigente? Só nesse caso o backend reescreve o CÓDIGO dos
   * cupons (o percentual faz parte do nome) — e só nesse caso o aviso faz sentido.
   */
  descontoPadraoAlterado(): boolean {
    const vigente = this.percentualPadraoVigente?.porcentagemDesconto;
    const novo = this.padraoForm.porcentagemDesconto;
    return vigente != null && novo != null && Number(vigente) !== Number(novo);
  }

  /** Desconto vigente sem casas decimais — é assim que ele aparece no código do cupom. */
  percentualPadraoVigenteFormatado(): string {
    return this.soParteInteira(this.percentualPadraoVigente?.porcentagemDesconto);
  }

  /** O desconto digitado, no mesmo formato em que entrará no código do cupom. */
  percentualPadraoNovoFormatado(): string {
    return this.soParteInteira(this.padraoForm.porcentagemDesconto);
  }

  /** O código do cupom carrega só a parte inteira do desconto (15,5% → "15"). */
  private soParteInteira(percentual: number | null | undefined): string {
    return percentual == null ? '' : String(Math.trunc(Number(percentual)));
  }

  /**
   * Salva o novo padrão. O backend aplica em massa nos cupons automáticos que ainda seguiam o
   * padrão anterior (renomeando o código quando o desconto muda), então a lista é recarregada
   * para já mostrar os códigos novos.
   */
  salvarPercentualPadrao(): void {
    this.padraoSubmetido = true;
    const f = this.padraoForm;
    if (f.porcentagemDesconto == null || f.porcentagemComissao == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('indicacoes.percentual_padrao.campos_obrigatorios'),
      });
      return;
    }

    this.salvandoPadrao = true;
    this.indicacaoService
      .alterarPercentualPadraoCupom({
        porcentagemDesconto: f.porcentagemDesconto,
        porcentagemComissao: f.porcentagemComissao,
      })
      .subscribe({
        next: (res) => {
          this.salvandoPadrao = false;
          this.padraoDialogVisivel = false;
          this.percentualPadraoVigente = res.padrao;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('indicacoes.percentual_padrao.sucesso'),
            detail: this.translate.instant('indicacoes.percentual_padrao.sucesso_detalhe', {
              atualizados: res.cuponsAtualizados,
              renomeados: res.cuponsRenomeados,
            }),
          });
          this.carregarCupons(0);
        },
        error: (err) => {
          this.salvandoPadrao = false;
          this.messageService.add({
            severity: 'error',
            summary:
              err?.error?.message ||
              this.translate.instant('indicacoes.percentual_padrao.erro'),
          });
        },
      });
  }

  /** Converte Date -> ISO `yyyy-MM-dd` (data local, sem timezone shift). */
  private toIsoDate(d: Date): string {
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /**
   * Rotulo localizado da origem detalhada da indicacao. Valores desconhecidos ou
   * ausentes retornam "-" (robustez contra respostas antigas sem o campo).
   */
  labelOrigem(origem: OrigemIndicacao | string | null | undefined): string {
    const chaves: Record<string, string> = {
      LINK: 'link',
      EMAIL: 'email',
      WHATSAPP: 'whatsapp',
      CUPOM: 'cupom',
      CONTADOR: 'contador',
    };
    const sufixo = origem ? chaves[String(origem).toUpperCase()] : undefined;
    if (!sufixo) {
      return '-';
    }
    return this.translate.instant(`indicacoes.origem.${sufixo}`);
  }

  /** Icone (PrimeIcons) correspondente a origem detalhada da indicacao. */
  iconeOrigem(origem: OrigemIndicacao | string | null | undefined): string {
    switch (origem ? String(origem).toUpperCase() : '') {
      case 'EMAIL':
        return 'pi pi-envelope';
      case 'LINK':
        return 'pi pi-link';
      case 'WHATSAPP':
        return 'pi pi-whatsapp';
      case 'CUPOM':
        return 'pi pi-ticket';
      case 'CONTADOR':
        return 'pi pi-briefcase';
      default:
        return '';
    }
  }

  /** Rotulo localizado da cadencia de repasse do cupom (MENSAL / A_VISTA). */
  tipoPeriodoLabel(valor: string | null): string {
    if (!valor) {
      return '';
    }
    const chave = `indicacoes.cupom_detalhe.periodo.${valor.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? valor : traduzido;
  }

  /** Traduz o status da indicacao (StatusIndicacao). */
  labelStatusIndicacao(status: StatusIndicacao | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Traduz o status de repasse resumido da indicacao (pode ser null). */
  labelStatusRepasseResumo(status: StatusRepasseResumo): string {
    if (!status) {
      return '-';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Traduz o status de repasse de uma comissao (drill-down). */
  labelStatusRepasse(status: StatusRepasse | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Severity do p-tag conforme o status (cor por significado). */
  severityStatusIndicacao(status: StatusIndicacao | string | undefined): TagSeverity {
    switch (status) {
      case 'CONFIRMADA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'CANCELADA':
        return 'secondary';
      default:
        return 'info';
    }
  }

  severityStatusRepasse(status: string | null | undefined): TagSeverity {
    switch (status) {
      case 'PAGA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'PARCIAL':
        return 'info';
      case 'CANCELADA':
        return 'secondary';
      default:
        return 'info';
    }
  }

  /** Linha de comissao CANCELADA — exibida esmaecida (historico). */
  isComissaoCancelada(comissao: ComissaoConsultaDTO): boolean {
    return comissao.statusRepasse === 'CANCELADA';
  }

  /** Converte competencia "AAAA-MM" em "MM/AAAA" (sem timezone). */
  formatarCompetencia(competencia: string | null | undefined): string {
    if (!competencia) {
      return '';
    }
    const partes = competencia.split('-');
    if (partes.length < 2) {
      return competencia;
    }
    return `${partes[1]}/${partes[0]}`;
  }

  /**
   * Rotulo da validade de um cupom (coluna "Validade" da listagem).
   * Convencao do backend: ano >= 2100 e a sentinela de "sem expiracao" — nesse
   * caso retorna string vazia para o template exibir o texto "Sem validade"
   * (estilo `cupom-promocional`). Caso contrario, formata em dd/MM/yyyy.
   */
  labelValidadeCupom(dataISO: string | null | undefined): string {
    if (this.cupomSemValidade(dataISO)) {
      return '';
    }
    return this.formatarData(dataISO);
  }

  /** True quando o cupom nao tem validade (sentinela ano >= 2100 ou data ausente/invalida). */
  cupomSemValidade(dataISO: string | null | undefined): boolean {
    if (!dataISO) {
      return true;
    }
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return true;
    }
    return data.getFullYear() >= 2100;
  }

  /** True quando o cupom esta vencido: data real (ano < 2100) anterior a agora. */
  cupomVencido(dataISO: string | null | undefined): boolean {
    if (!dataISO || this.cupomSemValidade(dataISO)) {
      return false; // sem validade / sentinela nunca vence
    }
    const data = new Date(dataISO);
    return data.getTime() < Date.now();
  }

  /** Formata data ISO 8601 em dd/MM/yyyy (padrao das telas do projeto). */
  formatarData(dataISO: string | null | undefined): string {
    if (!dataISO) {
      return '';
    }
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  // ===========================================================================
  // Util
  // ===========================================================================

  private toastErro(detalhe: string): void {
    this.messageService.add({
      severity: 'error',
      summary: detalhe,
    });
  }
}
